import { useCallback, useEffect, useState } from "react";
import {
    checkLlmProvider,
    listLlmModelAssignments,
    listLlmModels,
    listLlmProviders,
    LlmModelOut,
    ProviderStatusOut,
    ProviderTestResponse,
    PurposeOut,
    setLlmModelAssignment,
    syncLlmModels,
    updateLlmModel,
} from "@/api";
import { appState } from "@/app/appState";

const errorDetail = (error: unknown): string => {
    const detail = (error as { detail?: unknown })?.detail;
    return typeof detail === "string" ? detail : appState.t("chat.requestFailed");
};

const showError = (error: unknown) =>
    appState.setModal({ title: appState.t("common.error"), body: errorDetail(error) });

/** Decision models answer typed questions (Cortecs Decisions API) and cannot chat. */
export const isDecisionModel = (model: LlmModelOut) => model.supported_features.includes("decision");

/** Whether `model` has every capability `purpose` needs. Mirrors model_registry.missing_capabilities. */
// LEGACY_TOOL_JUDGE: eval.judge also accepts chat models through required_features; update with the backend.
export const fitsPurpose = (model: LlmModelOut, purpose: PurposeOut) =>
    purpose.required_modalities.every((m) => model.input_modalities.includes(m)) &&
    (isDecisionModel(model)
        ? !!purpose.accepts_decision
        : purpose.required_features.every((f) => model.supported_features.includes(f)));

export const isUsable = (model: LlmModelOut) => model.enabled && model.available;

export const useModelsAdmin = () => {
    const [providers, setProviders] = useState<ProviderStatusOut[]>([]);
    const [models, setModels] = useState<LlmModelOut[]>([]);
    const [purposes, setPurposes] = useState<PurposeOut[]>([]);
    const [tests, setTests] = useState<Record<string, ProviderTestResponse | "running">>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const isSuperAdmin = !!appState.user?.is_super_admin;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [p, m, a] = await Promise.all([
                listLlmProviders(),
                listLlmModels({ body: { enabled_only: false } }),
                listLlmModelAssignments(),
            ]);
            if (p.data) setProviders(p.data);
            if (m.data) setModels(m.data);
            if (a.data) setPurposes(a.data.purposes);
            const failed = [p, m, a].find((r) => r.error);
            if (failed) showError(failed.error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isSuperAdmin) load();
    }, [isSuperAdmin, load]);

    /** Run a mutation, surface its error, and reload what it may have changed. */
    const mutate = async <T,>(
        call: () => Promise<{ data?: T; error?: unknown }>,
        reload: () => Promise<void> = load,
    ): Promise<T | undefined> => {
        setSaving(true);
        try {
            const response = await call();
            if (response.error) {
                showError(response.error);
                return undefined;
            }
            await reload();
            return response.data;
        } finally {
            setSaving(false);
        }
    };

    const testProvider = async (provider: string) => {
        setTests((previous) => ({ ...previous, [provider]: "running" }));
        const response = await checkLlmProvider({ body: { provider } });
        setTests((previous) => {
            const next = { ...previous };
            if (response.data) next[provider] = response.data;
            else delete next[provider];
            return next;
        });
        if (response.error) showError(response.error);
    };

    const sync = async () => {
        const counts = await mutate(() => syncLlmModels());
        if (counts) {
            appState.setModal({
                title: appState.t("settings.models.syncDone"),
                body: appState.t("settings.models.syncCounts", {
                    fetched: counts.fetched,
                    added: counts.added,
                    removed: counts.removed,
                }),
            });
        }
    };

    const updateModel = (id: number, change: { enabled?: boolean; display_name?: string }) =>
        mutate(() => updateLlmModel({ body: { id, ...change } }));

    const assign = (purpose: string, modelId: number | undefined, fallbackIds: number[] = []) =>
        mutate(() =>
            setLlmModelAssignment({
                body: {
                    purpose,
                    llm_model_id: modelId ?? null,
                    fallback_llm_model_ids: modelId === undefined ? [] : fallbackIds,
                },
            }),
        );

    return {
        isSuperAdmin,
        providers,
        models,
        purposes,
        tests,
        loading,
        saving,
        load,
        testProvider,
        sync,
        updateModel,
        assign,
    };
};

export type ModelsAdmin = ReturnType<typeof useModelsAdmin>;
